/**
 * Money formatting for the UI. The API sends amounts as exact decimal STRINGS
 * with an explicit currency (never JS floats). We format for display using
 * Intl.NumberFormat but ALWAYS keep the raw string available so nothing lossy
 * happens to the underlying value. Formatting is presentation-only.
 */

export interface SerializedMoney {
  readonly amount: string;
  readonly currency: string;
}

/** Format a serialized-money value for display (2 dp by default). */
export function formatMoney(m: SerializedMoney | null | undefined, opts: { maximumFractionDigits?: number } = {}): string {
  if (!m) return "—";
  const n = Number(m.amount); // display only; the exact value stays in m.amount
  if (!Number.isFinite(n)) return `${m.amount} ${m.currency}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: m.currency,
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
    }).format(n);
  } catch {
    // Unknown currency code → fall back to a plain formatted number + code.
    return `${n.toLocaleString(undefined, { maximumFractionDigits: opts.maximumFractionDigits ?? 2 })} ${m.currency}`;
  }
}

/** Format a 0..1 ratio string/number as a percentage. */
export function formatPercent(ratio: string | number | null | undefined, dp = 1): string {
  if (ratio == null) return "—";
  const n = typeof ratio === "string" ? Number(ratio) : ratio;
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(dp)}%`;
}
